import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cytoscape from 'cytoscape';
import { fetchMemoryGraph } from '../api/memory';
import '../styles/memory.css';

/**
 * Header button + full-screen panel showing the workspace memory as a graph.
 *   Workspace tab          every conversation, the manual entities each was about, remembered facts and the
 *                          manual-graph relations between those entities
 *   This conversation tab  the turns in order, what each turn was about (sized by salience in working
 *                          memory), the pages it cited, and how those connect in the manual graph
 */

// one hue per node kind; resolved against the theme background so both themes stay readable
const KINDS = {
  workspace: { color: '#e60012', label: 'Workspace' },
  conversation: { color: '#2563eb', label: 'Conversation' },
  turn: { color: '#7c3aed', label: 'Question (turn)' },
  dtc: { color: '#ea580c', label: 'DTC' },
  component: { color: '#0d9488', label: 'Component' },
  connector: { color: '#0891b2', label: 'Connector' },
  section: { color: '#64748b', label: 'Manual section' },
  location: { color: '#65a30d', label: 'Location' },
  page: { color: '#a1a1aa', label: 'Cited page' },
  memory: { color: '#db2777', label: 'Remembered fact' },
};
const kindOf = (k) => KINDS[k] || { color: '#94a3b8', label: k };

function cssVar(name, fallback) {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

function buildStyle() {
  const text = cssVar('--cv-text', '#1f2629');
  const muted = cssVar('--cv-text-muted', '#5f686d');
  const bg = cssVar('--cv-bg', '#ffffff');
  const border = cssVar('--cv-border-strong', '#cbd0d3');
  return [
    {
      selector: 'node',
      style: {
        'background-color': 'data(color)',
        width: 'mapData(weight, 0, 1, 16, 54)',
        height: 'mapData(weight, 0, 1, 16, 54)',
        label: 'data(label)',
        color: text,
        'font-size': 10,
        'text-valign': 'bottom',
        'text-margin-y': 4,
        'text-wrap': 'ellipsis',
        'text-max-width': 150,
        'text-outline-color': bg,
        'text-outline-width': 2,
        'border-width': 2,
        'border-color': bg,
        'overlay-opacity': 0,
      },
    },
    { selector: 'node[kind = "workspace"], node[kind = "conversation"][?root]', style: { 'font-size': 12, 'font-weight': 600, shape: 'round-diamond' } },
    { selector: 'node[kind = "turn"]', style: { shape: 'round-rectangle', 'font-weight': 600 } },
    { selector: 'node[kind = "page"]', style: { shape: 'round-tag', 'font-size': 9, color: muted } },
    { selector: 'node[kind = "memory"]', style: { shape: 'star' } },
    { selector: 'node[?faded]', style: { opacity: 0.55, 'border-style': 'dashed', 'border-color': border } },
    { selector: 'node[?challenged]', style: { 'border-color': '#e5484d', 'border-width': 3 } },
    {
      selector: 'edge',
      style: {
        width: 'mapData(weight, 0, 1, 0.8, 3.5)',
        'line-color': border,
        'target-arrow-color': border,
        'target-arrow-shape': 'triangle',
        'arrow-scale': 0.7,
        'curve-style': 'bezier',
        opacity: 0.75,
        label: 'data(rel)',
        'font-size': 8,
        color: muted,
        'text-opacity': 0,
        'text-rotation': 'autorotate',
        'text-background-color': bg,
        'text-background-opacity': 1,
        'text-background-padding': 1,
      },
    },
    { selector: 'edge[rel = "NEXT"], edge[rel = "STARTS"]', style: { 'line-color': '#7c3aed', 'target-arrow-color': '#7c3aed', width: 3, opacity: 0.9 } },
    { selector: 'edge[group = "kg"]', style: { 'line-style': 'dashed', 'line-color': '#0d9488', 'target-arrow-color': '#0d9488', opacity: 0.6 } },
    { selector: '.dim', style: { opacity: 0.12, 'text-opacity': 0 } },
    { selector: 'edge.hl', style: { opacity: 1, 'text-opacity': 1, 'z-index': 10 } },
    { selector: 'node.hl', style: { opacity: 1 } },
    { selector: 'node:selected', style: { 'border-color': cssVar('--cv-focus', '#007bc6'), 'border-width': 4 } },
  ];
}

const MEMORY_RELS = new Set(['CONVERSATION', 'ABOUT', 'CITED', 'NEXT', 'STARTS', 'REMEMBERS', 'LEARNED', 'IN_FOCUS']);

function toElements(data, scope) {
  const nodes = (data.nodes || []).map((n) => ({
    data: {
      ...n.meta,
      id: n.id,
      kind: n.kind,
      label: n.label,
      title: n.title,
      weight: n.weight,
      color: kindOf(n.kind).color,
      root: (scope === 'conversation' && n.id === 'conv') || n.id === 'ws',
      faded: scope === 'conversation' && n.meta && n.meta.in_focus === false,
    },
  }));
  const edges = (data.edges || []).map((e) => ({
    data: { id: e.id, source: e.source, target: e.target, rel: e.rel, weight: e.weight, group: MEMORY_RELS.has(e.rel) ? 'memory' : 'kg' },
  }));
  return [...nodes, ...edges];
}

function Details({ node, scope, onOpenConversation, onClose }) {
  if (!node) {
    return (
      <div className="mg-details mg-details--empty">
        <p>Click a node to see what the assistant remembers about it.</p>
        <p className="mg-hint">Scroll to zoom · drag to pan · drag nodes to rearrange</p>
      </div>
    );
  }
  const d = node;
  const k = kindOf(d.kind);
  const rows = [
    ['Type', d.entity_type || k.label],
    d.key && ['Graph key', d.key],
    d.page_key && ['Page', d.page_key],
    d.mentions != null && ['Mentions', d.mentions],
    d.salience != null && scope === 'conversation' && ['Salience now', `${Math.round(d.salience * 100)}%${d.in_focus === false ? ' (faded from focus)' : ''}`],
    d.salience != null && scope === 'workspace' && d.salience > 0 && ['Peak salience', `${Math.round(d.salience * 100)}%`],
    d.intent && ['Intent', d.intent],
    d.mode && ['Mode', d.mode],
    d.turns != null && ['Turns', d.turns],
    (d.model || d.year) && ['Vehicle', [d.model, d.year].filter(Boolean).join(' ')],
    d.challenged && ['Note', 'This turn challenged the previous answer'],
  ].filter(Boolean);
  return (
    <div className="mg-details">
      <div className="mg-details__head">
        <span className="mg-dot" style={{ background: k.color }} />
        <span className="mg-details__kind">{d.entity_type || k.label}</span>
      </div>
      <div className="mg-details__title">{(d.title || d.label || '').split('\n')[0]}</div>
      {(d.title || '').split('\n').slice(1).join('\n').trim() && (
        <div className="mg-details__body">{(d.title || '').split('\n').slice(1).join('\n').trim()}</div>
      )}
      <dl className="mg-details__rows">
        {rows.map(([a, b]) => (
          <React.Fragment key={a}><dt>{a}</dt><dd>{String(b)}</dd></React.Fragment>
        ))}
      </dl>
      {d.kind === 'conversation' && d.conversation_id && scope === 'workspace' && (
        <button type="button" className="cv-btn cv-btn--small cv-btn--primary" onClick={() => { onOpenConversation(d.conversation_id); onClose(); }}>
          Open this conversation
        </button>
      )}
    </div>
  );
}

function GraphCanvas({ data, scope, onSelect, theme }) {
  const ref = useRef(null);
  const cyRef = useRef(null);

  useEffect(() => {
    if (!ref.current || !data) return undefined;
    const cy = cytoscape({
      container: ref.current,
      elements: toElements(data, scope),
      style: buildStyle(),
      layout: {
        name: 'cose',
        animate: false,
        randomize: true,
        nodeRepulsion: () => (scope === 'workspace' ? 14000 : 11000),
        idealEdgeLength: (e) => (e.data('group') === 'kg' ? 70 : e.data('rel') === 'NEXT' ? 120 : 95),
        edgeElasticity: () => 80,
        gravity: 0.35,
        numIter: 1500,
        padding: 30,
      },
      minZoom: 0.15,
      maxZoom: 3,
      wheelSensitivity: 0.25,
    });
    cyRef.current = cy;
    if (cy.zoom() > 1.25) { cy.zoom(1.25); cy.center(); }  // a small graph should not fill the screen
    const clear = () => cy.elements().removeClass('dim hl');
    cy.on('tap', 'node', (evt) => {
      const n = evt.target;
      const hood = n.closedNeighborhood();
      cy.elements().addClass('dim');
      hood.removeClass('dim').addClass('hl');
      onSelect(n.data());
    });
    cy.on('tap', (evt) => {
      if (evt.target === cy) { clear(); onSelect(null); }
    });
    cy.on('mouseover', 'node', (evt) => { evt.target.connectedEdges().addClass('hl'); ref.current.style.cursor = 'pointer'; });
    cy.on('mouseout', 'node', (evt) => {
      if (!evt.target.selected()) evt.target.connectedEdges().not('.hl-keep').removeClass('hl');
      ref.current.style.cursor = '';
    });
    return () => { cy.destroy(); cyRef.current = null; };
  }, [data, scope, onSelect, theme]);

  return (
    <div className="mg-canvas-wrap">
      <div ref={ref} className="mg-canvas" />
      <div className="mg-zoom">
        <button type="button" aria-label="Zoom in" onClick={() => cyRef.current && cyRef.current.zoom({ level: cyRef.current.zoom() * 1.25, renderedPosition: { x: ref.current.clientWidth / 2, y: ref.current.clientHeight / 2 } })}>+</button>
        <button type="button" aria-label="Zoom out" onClick={() => cyRef.current && cyRef.current.zoom({ level: cyRef.current.zoom() / 1.25, renderedPosition: { x: ref.current.clientWidth / 2, y: ref.current.clientHeight / 2 } })}>−</button>
        <button type="button" aria-label="Fit" onClick={() => cyRef.current && cyRef.current.fit(undefined, 30)}>⤢</button>
      </div>
    </div>
  );
}

function useThemeKey() {
  // re-render the canvas when the colour theme changes (ThemeToggle writes data-cv-theme on <html>)
  const [key, setKey] = useState(() => document.documentElement.getAttribute('data-cv-theme') || 'system');
  useEffect(() => {
    const obs = new MutationObserver(() => setKey(document.documentElement.getAttribute('data-cv-theme') || 'system'));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-cv-theme'] });
    return () => obs.disconnect();
  }, []);
  return key;
}

function MemoryPanel({ conversationId, onClose, onOpenConversation, translate }) {
  const t = translate;
  const [scope, setScope] = useState(conversationId ? 'conversation' : 'workspace');
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [selected, setSelected] = useState(null);
  const [reload, setReload] = useState(0);
  const theme = useThemeKey();

  useEffect(() => {
    let live = true;
    setSelected(null);
    if (scope === 'conversation' && !conversationId) {
      setState({ loading: false, error: null, data: null });
      return undefined;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchMemoryGraph(scope === 'conversation' ? conversationId : null)
      .then((data) => live && setState({ loading: false, error: null, data }))
      .catch((e) => live && setState({ loading: false, error: (e.response && e.response.data && e.response.data.detail) || e.message, data: null }));
    return () => { live = false; };
  }, [scope, conversationId, reload]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onSelect = useCallback((d) => setSelected(d), []);
  const { data } = state;
  const empty = data && (!data.nodes || data.nodes.length <= 1);
  const kindsPresent = useMemo(() => Object.keys((data && data.stats && data.stats.by_kind) || {}), [data]);

  return (
    <div className="mg-overlay" role="dialog" aria-modal="true" aria-label={t('Memory graph')} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="mg-panel">
        <div className="mg-header">
          <div className="mg-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="5" cy="6" r="2.5" /><circle cx="19" cy="6" r="2.5" /><circle cx="12" cy="18" r="2.5" /><path d="M7.2 7.2l3.6 8.6M16.8 7.2l-3.6 8.6M7.5 6h9" />
            </svg>
            <span>{t('Memory graph')}</span>
          </div>
          <div className="mg-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={scope === 'workspace'} className={scope === 'workspace' ? 'is-active' : ''} onClick={() => setScope('workspace')}>
              {t('Whole workspace')}
            </button>
            <button type="button" role="tab" aria-selected={scope === 'conversation'} className={scope === 'conversation' ? 'is-active' : ''} onClick={() => setScope('conversation')}>
              {t('This conversation')}
            </button>
          </div>
          <div className="mg-header__actions">
            {data && data.stats && <span className="mg-stats">{data.stats.nodes} {t('nodes')} · {data.stats.edges} {t('edges')}</span>}
            <button type="button" className="cv-icon-btn" aria-label={t('Refresh')} title={t('Refresh')} onClick={() => setReload((n) => n + 1)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-2.6-6.4" /><path d="M21 3v6h-6" /></svg>
            </button>
            <button type="button" className="cv-icon-btn" aria-label={t('Close')} title={t('Close')} onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
        <p className="mg-sub">
          {scope === 'workspace'
            ? t('Everything the assistant remembers for you: your conversations, the parts, DTCs and sections they were about, the facts it learned, and how those connect in the manual.')
            : t('How the assistant followed this conversation: each question in order, what it was about (bigger = more salient in its working memory right now), the pages it used, and the manual links between them.')}
        </p>
        <div className="mg-body">
          <div className="mg-main">
            {state.loading && <div className="mg-state"><span className="cv-spinner" /> {t('Loading memory…')}</div>}
            {!state.loading && state.error && <div className="mg-state mg-state--error">{String(state.error)}</div>}
            {!state.loading && !state.error && scope === 'conversation' && !conversationId && (
              <div className="mg-state">{t('Start or open a conversation to see its memory graph.')}</div>
            )}
            {!state.loading && !state.error && data && empty && (
              <div className="mg-state">{scope === 'workspace' ? t('Nothing remembered yet. Ask a few questions first.') : t('This conversation has no remembered turns yet.')}</div>
            )}
            {!state.loading && !state.error && data && !empty && (
              <GraphCanvas data={data} scope={scope} onSelect={onSelect} theme={theme} />
            )}
          </div>
          <aside className="mg-side">
            <Details node={selected} scope={scope} onOpenConversation={onOpenConversation} onClose={onClose} />
            {kindsPresent.length > 0 && (
              <div className="mg-legend">
                <div className="cv-section-label">{t('Legend')}</div>
                {kindsPresent.map((k) => (
                  <div key={k} className="mg-legend__row">
                    <span className="mg-dot" style={{ background: kindOf(k).color }} />
                    <span>{kindOf(k).label}</span>
                    <span className="mg-legend__n">{data.stats.by_kind[k]}</span>
                  </div>
                ))}
                <div className="mg-legend__row"><span className="mg-line mg-line--solid" /><span>{t('Memory link')}</span></div>
                <div className="mg-legend__row"><span className="mg-line mg-line--dashed" /><span>{t('Manual knowledge-graph link')}</span></div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

export default function MemoryGraphButton({ conversationId, onOpenConversation, translate = (s) => s }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  return (
    <>
      <button type="button" className="mg-open-btn" onClick={() => setOpen(true)} title={translate('Memory graph')}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="5" cy="6" r="2.5" /><circle cx="19" cy="6" r="2.5" /><circle cx="12" cy="18" r="2.5" /><path d="M7.2 7.2l3.6 8.6M16.8 7.2l-3.6 8.6M7.5 6h9" />
        </svg>
        <span>{translate('Memory')}</span>
      </button>
      {open && (
        <MemoryPanel conversationId={conversationId} onClose={close} onOpenConversation={onOpenConversation} translate={translate} />
      )}
    </>
  );
}
